import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import JsonLd, { webAppSchema } from 'shared/JsonLd';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import ToolLeadCapture from '../../components/ToolLeadCapture/ToolLeadCapture';

// Per CTCAC §10325(c). Category maximums shown; checklists sum and cap at the
// category max. Lowest Income uses a guided slider (its real formula is a
// weighted-average calculation). Totals are an estimate of the MAJOR categories.
const EXPERIENCE = [
    { id: 'gp', pts: 7, label: 'General Partner has 3+ tax-credit projects in service 3+ years', note: '5–7 pts by portfolio size' },
    { id: 'mgmt', pts: 3, label: 'Management company has 6+ projects / 3+ years experience', note: '2–3 pts by portfolio size' },
];
const SITE_AMENITIES = [
    { id: 'transit', pts: 4, label: 'Transit stop / corridor within distance', note: '3–7 pts by service level + bus-pass option' },
    { id: 'grocery', pts: 5, label: 'Full-scale grocery / supermarket', note: '3–5 pts by size & distance' },
    { id: 'park', pts: 3, label: 'Public park or community center' },
    { id: 'library', pts: 3, label: 'Public library' },
    { id: 'medical', pts: 3, label: 'Medical clinic or hospital' },
    { id: 'school', pts: 3, label: 'Public school (family projects)' },
    { id: 'senior', pts: 3, label: 'Senior center (senior projects)' },
    { id: 'internet', pts: 2, label: 'Free high-speed internet to each unit', note: '2 pts (3 for Rural set-aside) — see the Broadband Estimator' },
    { id: 'pharmacy', pts: 2, label: 'Pharmacy' },
    { id: 'highresource', pts: 8, label: 'New-construction Large Family project in a High/Highest Resource area (CTCAC/HCD Opportunity Map)' },
];
const SERVICE_AMENITIES = [
    { id: 'coordinator', pts: 5, label: 'Service Coordinator (1 FTE / 600 bedrooms)' },
    { id: 'specialist', pts: 5, label: 'Services Specialist (1 FTE / 600 bedrooms)' },
    { id: 'classes', pts: 7, label: 'Instructor-led classes (84 hrs/yr)' },
    { id: 'health', pts: 5, label: 'Health & wellness services' },
    { id: 'childcare', pts: 5, label: 'Licensed childcare' },
    { id: 'afterschool', pts: 5, label: 'After-school program' },
];
const READINESS = [
    { id: 'sitecontrol', pts: 2, label: 'Site control documented' },
    { id: 'zoning', pts: 3, label: 'Zoning / general plan allows the project as proposed' },
    { id: 'ceqa', pts: 3, label: 'CEQA / environmental clearance complete' },
    { id: 'financing', pts: 2, label: 'Enforceable construction financing commitments' },
];
const MISC = [
    { id: 'smokefree', pts: 2, label: 'Smoke-free residential policy' },
    { id: 'accessibility', pts: 2, label: 'Enhanced accessibility / universal design' },
    { id: 'revital', pts: 2, label: 'Local revitalization-area plan (QCT / <60% AMI tract / Promise Zone)' },
    { id: 'surplusland', pts: 2, label: 'Built on excess state-owned or local surplus land' },
    { id: 'creditsub', pts: 2, label: 'Federal ↔ state tax credit exchange (credit substitution)' },
    { id: 'historic', pts: 1, label: 'Historic preservation (uses Historic Tax Credits)' },
    { id: 'tenantownership', pts: 1, label: 'Eventual tenant ownership plan' },
];

const CAPS = { experience: 10, housing: 10, site: 15, service: 10, income: 52, readiness: 10, misc: 2 };
const TOTAL_MAX = Object.values(CAPS).reduce((a, b) => a + b, 0); // 108

const sumPicked = (list, picked, cap) => {
    let s = 0;
    list.forEach((it) => { if (picked[it.id]) s += it.pts; });
    return Math.min(cap, s);
};

const Checklist = ({ items, picked, onToggle }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((it) => (
            <label key={it.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: '.9rem' }}>
                <input type="checkbox" checked={!!picked[it.id]} onChange={() => onToggle(it.id)} style={{ marginTop: 4 }} />
                <span>
                    <strong>{it.label}</strong> <span style={{ color: 'var(--bs-primary)', fontWeight: 700 }}>+{it.pts}</span>
                    {it.note && <span style={{ display: 'block', fontSize: '.78rem', color: '#6c757d' }}>{it.note}</span>}
                </span>
            </label>
        ))}
    </div>
);

const SectionCard = ({ title, points, max, children }) => (
    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '20px 24px', marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <h3 className="m-0" style={{ fontSize: '1.05rem' }}>{title}</h3>
            <span style={{ fontWeight: 700, color: 'var(--bs-primary)' }}>{points} / {max}</span>
        </div>
        {children}
    </div>
);

const TcacScoringEstimatorPage = () => {
    usePageSeo({
        title: 'TCAC 9% Scoring Estimator — Self-Score Your LIHTC Application',
        description: 'Free CTCAC 9% scoring estimator. Self-score every major §10325(c) category — experience, amenities, lowest income, and readiness — out of 108 points.',
        keywords: 'TCAC scoring estimator, CTCAC 9% points calculator, LIHTC self score, 10325 scoring, lowest income points, site amenities points, service amenities points, readiness to proceed, affordable housing tax credit California, BUILDLAB',
    });

    const [pick, setPick] = useState({});
    const [housing, setHousing] = useState(false);
    const [income, setIncome] = useState(45);

    const toggle = (id) => setPick((p) => ({ ...p, [id]: !p[id] }));

    const scores = useMemo(() => {
        const experience = sumPicked(EXPERIENCE, pick, CAPS.experience);
        const site = sumPicked(SITE_AMENITIES, pick, CAPS.site);
        const service = sumPicked(SERVICE_AMENITIES, pick, CAPS.service);
        const readiness = sumPicked(READINESS, pick, CAPS.readiness);
        const misc = sumPicked(MISC, pick, CAPS.misc);
        const housingPts = housing ? CAPS.housing : 0;
        const incomePts = Math.min(CAPS.income, income);
        const total = experience + housingPts + site + service + incomePts + readiness + misc;
        return { experience, site, service, readiness, misc, housing: housingPts, income: incomePts, total };
    }, [pick, housing, income]);

    const pct = Math.round((scores.total / TOTAL_MAX) * 100);

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'TCAC 9% Scoring Estimator'} pagesub={'Estimator'} pageTop={'Affordable Housing'} />
                <JsonLd data={webAppSchema({ name: 'TCAC 9% Scoring Estimator', description: 'Free CTCAC 9% scoring estimator — self-score every major §10325(c) category out of 108 points.', path: '/tcac-scoring-estimator' })} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Self-score your 9% LIHTC application</h2>
                                <p>
                                    The 9% credit is fiercely competitive — winning applications usually max nearly every category.
                                    Work through the major <strong>CTCAC §10325(c)</strong> scoring categories below to estimate your
                                    total. Then talk to us about the technology line-items (broadband, community WiFi, security, EV,
                                    accessibility) that earn points across Site and Service Amenities.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Sections */}
                            <div className="col-lg-7">
                                <SectionCard title="Developer & Management Experience" points={scores.experience} max={CAPS.experience}>
                                    <Checklist items={EXPERIENCE} picked={pick} onToggle={toggle} />
                                </SectionCard>

                                <SectionCard title="Housing Needs" points={scores.housing} max={CAPS.housing}>
                                    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', fontSize: '.9rem' }}>
                                        <input type="checkbox" checked={housing} onChange={() => setHousing((v) => !v)} style={{ marginTop: 4 }} />
                                        <span>
                                            <strong>Project meets its housing-needs set-aside</strong> (Large Family, Senior, Special Needs, At-Risk, or SRO) <span style={{ color: 'var(--bs-primary)', fontWeight: 700 }}>+10</span>
                                            <span style={{ display: 'block', fontSize: '.78rem', color: '#6c757d' }}>Awarded in one category based on your set-aside.</span>
                                        </span>
                                    </label>
                                </SectionCard>

                                <SectionCard title="Site Amenities (cap 15)" points={scores.site} max={CAPS.site}>
                                    <Checklist items={SITE_AMENITIES} picked={pick} onToggle={toggle} />
                                </SectionCard>

                                <SectionCard title="Service Amenities (cap 10)" points={scores.service} max={CAPS.service}>
                                    <Checklist items={SERVICE_AMENITIES} picked={pick} onToggle={toggle} />
                                </SectionCard>

                                <SectionCard title="Lowest Income" points={scores.income} max={CAPS.income}>
                                    <p style={{ fontSize: '.85rem', color: '#555' }}>
                                        Points scale with the share of units at deep affordability (30–50% AMI). Most competitive
                                        applications land 45–52. Estimate where you'll commit:
                                    </p>
                                    <input
                                        type="range" min="0" max="52" value={income}
                                        onChange={(e) => setIncome(parseInt(e.target.value, 10))}
                                        className="form-range" aria-label="Lowest income points"
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', color: '#6c757d' }}>
                                        <span>0</span><span>45 (typical floor)</span><span>52 (max)</span>
                                    </div>
                                </SectionCard>

                                <SectionCard title="Readiness to Proceed (cap 10)" points={scores.readiness} max={CAPS.readiness}>
                                    <Checklist items={READINESS} picked={pick} onToggle={toggle} />
                                </SectionCard>

                                <SectionCard title="Miscellaneous Federal & State Policies (cap 2)" points={scores.misc} max={CAPS.misc}>
                                    <Checklist items={MISC} picked={pick} onToggle={toggle} />
                                </SectionCard>
                            </div>

                            {/* Running total */}
                            <div className="col-lg-5">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '28px 30px', color: '#fff', position: 'sticky', top: 100 }}>
                                    <div style={{ fontSize: '.85rem', opacity: 0.8 }}>Estimated competitive score</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 4px' }}>
                                        <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: pct >= 90 ? '#7CFFB2' : pct >= 75 ? '#BFF59A' : '#FFC9A3' }}>{scores.total}</span>
                                        <span style={{ fontSize: '1.1rem', opacity: 0.7 }}>/ {TOTAL_MAX}</span>
                                    </div>
                                    <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,.15)', overflow: 'hidden', margin: '8px 0 14px' }}>
                                        <div style={{ height: '100%', width: `${pct}%`, background: '#B85C38' }} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '5px 12px', fontSize: '.85rem' }}>
                                        <span style={{ opacity: 0.8 }}>Experience</span><strong style={{ textAlign: 'right' }}>{scores.experience}/{CAPS.experience}</strong>
                                        <span style={{ opacity: 0.8 }}>Housing needs</span><strong style={{ textAlign: 'right' }}>{scores.housing}/{CAPS.housing}</strong>
                                        <span style={{ opacity: 0.8 }}>Site amenities</span><strong style={{ textAlign: 'right' }}>{scores.site}/{CAPS.site}</strong>
                                        <span style={{ opacity: 0.8 }}>Service amenities</span><strong style={{ textAlign: 'right' }}>{scores.service}/{CAPS.service}</strong>
                                        <span style={{ opacity: 0.8 }}>Lowest income</span><strong style={{ textAlign: 'right' }}>{scores.income}/{CAPS.income}</strong>
                                        <span style={{ opacity: 0.8 }}>Readiness</span><strong style={{ textAlign: 'right' }}>{scores.readiness}/{CAPS.readiness}</strong>
                                        <span style={{ opacity: 0.8 }}>Misc policies</span><strong style={{ textAlign: 'right' }}>{scores.misc}/{CAPS.misc}</strong>
                                    </div>
                                    <p style={{ fontSize: '.82rem', opacity: 0.78, marginTop: 12 }}>
                                        Covers the major scoring categories. Tiebreakers and negative points are not modeled.
                                    </p>
                                    <Link to="/contact" className="btn btn-primary w-100 mt-2" style={{ display: 'block', textAlign: 'center' }}>
                                        <span className="btn_label" data-text="Maximize our amenity points">Maximize our amenity points</span>
                                    </Link>
                                    <div style={{ fontSize: '.8rem', opacity: 0.8, marginTop: 12 }}>
                                        Drill into a category:{' '}
                                        <Link to="/tcac-broadband-estimator" style={{ color: '#FFC9A3' }}>Broadband Estimator</Link>
                                    </div>
                                    <ToolLeadCapture
                                        tool="TCAC 9% Scoring Estimator"
                                        summary={`Estimated 9% score ${scores.total} / ${TOTAL_MAX} — lowest income ${scores.income}, site amenities ${scores.site}, service amenities ${scores.service}, readiness ${scores.readiness}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <p className="small text-muted mt-3" style={{ maxWidth: 880 }}>
                            Source: CTCAC §10325(c), 9% competitive scoring (regulations adopted December 10, 2025). Category maximums
                            shown are summarized for planning and reflect top-tier point values only; point thresholds, distances, and the
                            Lowest Income formula change between QAP rounds and are verified by CTCAC. This is not scoring advice — confirm
                            every line with your tax-credit consultant and the current regulations. BUILDLAB designs the broadband, WiFi, security, and accessibility infrastructure that
                            earns Site and Service Amenity points.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default TcacScoringEstimatorPage;
